import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DDC_NAMES from '../data/ddcNames';
import '../styles/DDCBrowserPage.css';

const PAGE_SIZE = 20;

function getGroupKey(callnum, range) {
  if (!callnum) return null;
  const r = range || '';

  // Top level → single digit (0–9)
  if (r.length === 0) return callnum[0];

  // Main class (1 digit) → division (XX0): '621.3' at '6' → '620'
  if (r.length === 1) {
    if (callnum.length < 3) return null;
    return callnum[0] + callnum[1] + '0';
  }

  // Division (XX0) → section (XXX): '621.3' at '620' → '621'
  if (r.length === 3 && r.endsWith('0')) {
    if (callnum.length < 3) return null;
    return callnum.substring(0, 3);
  }

  // Section (XXX, not ending in 0) → first decimal: '621.3815' at '621' → '621.3'
  if (r.length === 3) {
    const dot = callnum.indexOf('.');
    if (dot < 0) return callnum;
    return callnum.substring(0, dot + 2);
  }

  // Decimal levels → one character deeper
  const nextLen = r.length + 1;
  return callnum.length >= nextLen ? callnum.substring(0, nextLen) : callnum;
}

// Returns the prefix that all sub-keys of `range` must start with
function getParentPrefix(range) {
  if (!range) return '';
  if (range.length === 1) return range;
  if (range.length === 3 && range.endsWith('0')) return range.substring(0, 2); // '620' → '62'
  return range;
}

// Returns the API query prefix for a given range.
// Division-level ranges (XX0) must use 2 chars so LIKE covers all XX0–XX9 sections.
// e.g. '010' → '01' (covers 010, 011, 012 … 019), '621' → '621' (unchanged)
function apiPrefix(range) {
  if (range.length === 3 && range.endsWith('0')) return range.substring(0, 2);
  return range;
}

function getName(key) {
  if (!key) return key;
  if (DDC_NAMES[key]) return DDC_NAMES[key];
  if (key.length === 1) return DDC_NAMES[key] || DDC_NAMES[key + '00'] || key;
  for (let l = key.length - 1; l >= 3; l--) {
    if (DDC_NAMES[key.substring(0, l)]) return DDC_NAMES[key.substring(0, l)];
  }
  const division = key.substring(0, 2) + '0';
  if (DDC_NAMES[division]) return DDC_NAMES[division];
  return DDC_NAMES[key[0] + '00'] || key;
}

function buildCrumbs(range) {
  if (!range) return [];
  const crumbs = [];
  const add = (r, label) => {
    if (crumbs.length && crumbs[crumbs.length - 1].label === label) return;
    crumbs.push({ range: r, label });
  };

  // Main class (single digit)
  add(range[0], getName(range[0]));

  if (range.length >= 3) {
    // Division level (XX0)
    const division = range[0] + range[1] + '0';
    add(division, getName(division));

    // Section level (if not itself a division, i.e., doesn't end in 0)
    if (!range.substring(0, 3).endsWith('0')) {
      add(range.substring(0, 3), getName(range.substring(0, 3)));
    }
  }

  // Decimal levels
  if (range.length > 3) {
    const dot = range.indexOf('.');
    if (dot > 0) {
      for (let l = dot + 2; l <= range.length; l++) {
        add(range.substring(0, l), getName(range.substring(0, l)));
      }
    }
  }

  // Current range is shown in h1, not in crumbs
  if (crumbs.length && crumbs[crumbs.length - 1].range === range) crumbs.pop();
  // Also remove last crumb if it has the same label as the current page (fallback-name case)
  const pageLabel = getName(range);
  if (crumbs.length && crumbs[crumbs.length - 1].label === pageLabel) crumbs.pop();
  return crumbs;
}

function buildSubtopics(items, range) {
  const parentPrefix = getParentPrefix(range);
  const counts = {};
  let baseCount = 0; // books filed at exactly this level (no sub-section)

  items.forEach(({ callnum, bookCount }) => {
    const key = getGroupKey(callnum, range);
    if (!key) return;
    if (key === range) { baseCount += bookCount; return; } // filed at this exact level
    if (parentPrefix && !key.startsWith(parentPrefix)) return;
    counts[key] = (counts[key] || 0) + bookCount;
  });

  const currentRangeName = getName(range);
  const raw = Object.entries(counts)
    .map(([key, count]) => ({ key, name: getName(key), count }))
    .sort((a, b) => a.key.localeCompare(b.key));
  const deduped = [];
  const seen = new Map();
  for (const item of raw) {
    // Never merge chips whose name falls back to the current page name —
    // each one is a distinct navigable key and merging them hides subtopics.
    if (item.name !== currentRangeName && seen.has(item.name)) {
      deduped[seen.get(item.name)].count += item.count;
    } else {
      if (item.name !== currentRangeName) seen.set(item.name, deduped.length);
      deduped.push({ ...item });
    }
  }
  return { subtopics: deduped, baseCount };
}

export default function DDCBrowserPage() {
  const { range = '' } = useParams();
  const navigate = useNavigate();

  const [topicsLoading, setTopicsLoading] = useState(true);
  const [subtopics, setSubtopics] = useState([]);
  const [baseCount, setBaseCount] = useState(0);

  const [booksLoading, setBooksLoading] = useState(false);
  const [books, setBooks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  // Reset page to 1 immediately in render phase when DDC range changes
  // (Prevents double fetching and pagination race conditions)
  const prevRangeRef = React.useRef(range);
  if (prevRangeRef.current !== range) {
    prevRangeRef.current = range;
    setPage(1);
  }

  // Fetch sub-topics whenever range changes
  useEffect(() => {
    setTopicsLoading(true);
    setSubtopics([]); // Clear stale sub-topics immediately
    setBaseCount(0);
    const ctrl = new AbortController();
    fetch(`/api/ddc/browse?range=${encodeURIComponent(apiPrefix(range))}`, { signal: ctrl.signal })
      .then(r => r.json())
      .then(({ items = [] }) => {
        const { subtopics: built, baseCount: bc } = buildSubtopics(items, range);
        setSubtopics(built);
        setBaseCount(bc);
        setTopicsLoading(false);
      })
      .catch(e => {
        if (e.name !== 'AbortError') {
          setSubtopics([]);
          setBaseCount(0);
          setTopicsLoading(false);
        }
      });
    return () => ctrl.abort();
  }, [range]);

  // Fetch books; reset to page 1 whenever range changes
  useEffect(() => {
    if (!range) { setBooks([]); setTotal(0); setPage(1); setBaseCount(0); return; }
    const ctrl = new AbortController();
    setBooksLoading(true);
    setBooks([]); // Clear stale book lists immediately
    setTotal(0);
    fetch(
      `/api/books/by-callnumber?prefix=${encodeURIComponent(apiPrefix(range))}&limit=${PAGE_SIZE}&page=${page}`,
      { signal: ctrl.signal }
    )
      .then(r => r.json())
      .then(data => {
        setBooks(data.books || []);
        setTotal(data.total || 0);
        setBooksLoading(false);
      })
      .catch(e => {
        if (e.name !== 'AbortError') {
          setBooks([]);
          setTotal(0);
          setBooksLoading(false);
        }
      });
    return () => ctrl.abort();
  }, [range, page]);

  const goUp = () => {
    if (!range) { navigate('/'); return; }
    if (range.length <= 1) { navigate('/discover'); return; }
    if (range.length === 3) {
      if (range.endsWith('0')) {
        navigate('/discover/' + range[0]);           // division → main class: '620' → '6'
      } else {
        navigate('/discover/' + range[0] + range[1] + '0'); // section → division: '621' → '620'
      }
      return;
    }
    let up = range.substring(0, range.length - 1);
    if (up.endsWith('.')) up = up.slice(0, -1); // strip trailing dot: '621.' → '621'
    navigate('/discover/' + up);
  };

  const crumbs = buildCrumbs(range);
  const currentName = range
    ? `${getName(range)}${DDC_NAMES[range] ? '' : ` (${range})`}`
    : 'Browse All Subjects';
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="ddc-page">

      {/* Breadcrumb */}
      <nav className="ddc-breadcrumb" aria-label="Subject navigation">
        <button className="ddc-breadcrumb__item" onClick={() => navigate('/discover')}>
          All Subjects
        </button>
        {crumbs.map((c) => (
          <React.Fragment key={c.range}>
            <span className="ddc-breadcrumb__sep" aria-hidden="true">›</span>
            <button className="ddc-breadcrumb__item" onClick={() => navigate('/discover/' + c.range)}>
              {c.label}
            </button>
          </React.Fragment>
        ))}
      </nav>

      {/* Page header */}
      <div className="ddc-page__header">
        {range && <button className="ddc-back-btn" onClick={goUp}>← Back</button>}
        <h1 className="ddc-page__title">{currentName}</h1>
      </div>

      {/* ── TOP LEVEL: big tiles ── */}
      {!range && (
        topicsLoading
          ? <p className="ddc-page__state">Loading…</p>
          : <div className="ddc-grid">
              {subtopics.map(({ key, name, count }) => (
                <button key={key} className="ddc-card" onClick={() => navigate('/discover/' + key)}>
                  <span className="ddc-card__name">{name}</span>
                  <span className="ddc-card__count">{count.toLocaleString()} books</span>
                </button>
              ))}
            </div>
      )}

      {/* ── DEEPER LEVELS: chips + books ── */}
      {range && (
        <>
          {/* Sub-topic chips */}
          {!topicsLoading && subtopics.length > 0 && (
            <section className="ddc-subtopics">
              <p className="ddc-subtopics__label">Narrow by sub-topic</p>
              <div className="ddc-chips">
                {subtopics.map(({ key, name, count }) => (
                  <button
                    key={key}
                    className="ddc-chip"
                    onClick={() => navigate('/discover/' + key)}
                  >
                    {name === getName(range) ? `Further sub-topics (${key})` : name}
                    <span className="ddc-chip__count">{count.toLocaleString()}</span>
                  </button>
                ))}
                {baseCount > 0 && (
                  <span className="ddc-chip ddc-chip--general" title="Books filed at this level without further subdivision">
                    General
                    <span className="ddc-chip__count">{baseCount.toLocaleString()}</span>
                  </span>
                )}
              </div>
            </section>
          )}

          {/* Books */}
          <section className="ddc-books-section">
            {total > 0 && (
              <h2 className="ddc-books__heading">
                {total.toLocaleString()} book{total !== 1 ? 's' : ''} in this subject
              </h2>
            )}
            {booksLoading && <p className="ddc-page__state">Loading books…</p>}
            {!booksLoading && books.length === 0 && total === 0 && (
              <p className="ddc-page__empty">No books found in this section.</p>
            )}
            <ul className="ddc-books__list">
              {books.map((book) => (
                <li key={book.biblio_id}>
                  <Link to={`/book/${book.biblio_id}`} className="ddc-book-row">
                    <span className="ddc-book-row__title">{book.title}</span>
                    {book.author && (
                      <span className="ddc-book-row__author">{book.author}</span>
                    )}
                    {Number(book.available_count) > 0 && (
                      <span className="ddc-book-row__avail">Available</span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="ddc-pagination">
                <button
                  className="ddc-pagination__btn"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  ← Prev
                </button>
                <span className="ddc-pagination__info">
                  Page {page} of {totalPages}
                </span>
                <button
                  className="ddc-pagination__btn"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next →
                </button>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
